import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Trophy,
  Gift,
  BadgeCheck,
  Check,
  ArrowRight,
  ListOrdered,
  Radio,
  Timer,
} from "lucide-react";
import { toast } from "sonner";
import { SITE, STEPS, BONUS_PERKS, CLAIM_STEPS } from "../mock";
import Reveal from "../components/site/Reveal";
import { CrownIcon } from "../components/site/Navbar";
import { XIcon, KickIcon, DiscordIcon } from "../components/site/icons";

const SectionHeading = ({ badge, badgeIcon, title, subtitle, lit = false }) => (
  <div className="text-center flex flex-col items-center">
    <span className="section-badge">
      {badgeIcon}
      {badge}
    </span>
    <h2 className={`heading-3d ${lit ? "lit-title" : ""} text-[2.2rem] sm:text-[3rem] lg:text-[3.4rem] mt-5`}>{title}</h2>
    {subtitle && (
      <p className="mt-4 max-w-xl text-[0.98rem] sm:text-[1.02rem] leading-relaxed text-[#bcd9f2]">{subtitle}</p>
    )}
  </div>
);

const Hero = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url(/img/hero_scene.png)",
          transform: `translateY(${offset * 0.22}px) scale(1.06)`,
          willChange: "transform",
        }}
      />
      <img
        alt=""
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-full object-cover object-top pointer-events-none select-none"
        src="/img/hero_fg.png"
        style={{
          height: "62%",
          transform: `translateY(${offset * 0.1}px)`,
          willChange: "transform",
          maskImage: "linear-gradient(transparent 0%, black 18%, black 76%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(transparent 0%, black 18%, black 76%, transparent 100%)",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[#04274e]/[0.92] via-[#04274e]/40 to-transparent pointer-events-none z-[5]" />
      <div className="absolute bottom-0 left-0 right-0 h-72 bg-gradient-to-b from-transparent via-[#0a60ab]/70 to-[#0a60ab] pointer-events-none" />
      <svg
        className="absolute bottom-0 left-0 w-full h-24 pointer-events-none"
        viewBox="0 0 1440 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path d="M0,60 C240,100 480,20 720,55 C960,90 1200,30 1440,60 L1440,100 L0,100 Z" fill="#0a60ab" opacity="0.85" />
        <path d="M0,78 C240,110 480,45 720,72 C960,98 1200,55 1440,78 L1440,100 L0,100 Z" fill="#0a60ab" />
      </svg>
      <div className="relative z-10 flex flex-col items-center text-center px-5 pt-16">
        <h1 className="heading-3d mt-5 text-[2.6rem] sm:text-[4rem] lg:text-[5.2rem] max-w-5xl" style={{ lineHeight: 1.05 }}>
          Rule the reef,
          <br />
          claim your rewards
        </h1>
        <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
          <Link className="btn-pill btn-blue min-w-[230px]" to="/leaderboard">
            <Trophy className="w-[18px] h-[18px]" strokeWidth={2.4} />
            Leaderboard
          </Link>
          <a href="/#bonuses" className="btn-pill btn-teal min-w-[230px]">
            <Gift className="w-[18px] h-[18px]" strokeWidth={2.4} />
            Browse Rewards
          </a>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 opacity-90">
        <div className="mouse-indicator">
          <span></span>
        </div>
        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
          <path d="M1 1l6 6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section className="relative z-10 py-24 sm:py-28 overflow-hidden">
    <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
      <div
        className="sub-spotlight"
        style={{ top: "4rem", left: "50%", width: "780px", height: "360px", transform: "translateX(-50%)" }}
      />
      <img
        alt=""
        className="sub-drift absolute select-none"
        src="/img/submarine.png"
        style={{ top: "1.5rem", left: "calc(50% - 640px)", width: "260px", maxWidth: "26vw" }}
      />
    </div>
    <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          badge="How it works"
          title="Four strokes to the surface"
          subtitle="No paperwork, no waiting rooms. Link your account and every wager starts counting instantly."
          lit
        />
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((step, i) => (
          <Reveal key={step.num} delay={i * 120}>
            <div className="glass-card step-card p-7 h-full">
              <span className="step-num">{step.num}</span>
              <h3 className="font-display font-bold text-white text-[1.35rem] mt-4">{step.title}</h3>
              <p className="mt-2 text-[0.92rem] leading-relaxed text-[#b3d2ee]">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Bonus = () => {
  const copyCode = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(SITE.code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = SITE.code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`Code "${SITE.code}" copied to clipboard!`);
    } catch (e) {
      toast.error("Could not copy the code");
    }
  };

  return (
    <section className="relative z-10 py-24 sm:py-28 scroll-mt-20" id="bonuses">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            badge="Bonus"
            title="Claim instantly & enjoy"
            subtitle={`${SITE.partner.name} is the one and only official partner. Redeem the code \u201c${SITE.code}\u201d to unlock rakeback, VIP perks and leaderboard entry.`}
          />
        </Reveal>
        <Reveal delay={150}>
          <div className="glass-card mt-14 p-7 sm:p-10 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-300/40 bg-yellow-300/10 text-yellow-200 text-[0.68rem] font-bold tracking-[0.22em] uppercase">
                <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
                Official Partner
              </span>
              <div className="mt-6 flex items-center gap-4">
                <span className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center p-2 shadow-[0_10px_26px_rgba(2,20,44,0.45)] shrink-0">
                  <img alt={SITE.partner.name} className="w-full h-auto object-contain" src={SITE.partner.logo} />
                </span>
                <div>
                  <h3 className="heading-3d-sm text-[1.9rem] sm:text-[2.2rem]">{SITE.partner.name}</h3>
                  <p className="text-[#9fc5e8] text-[0.8rem] font-bold tracking-[0.2em] uppercase mt-1">
                    Use code “{SITE.code}”
                  </p>
                </div>
              </div>
              <ul className="mt-7 space-y-3.5">
                {BONUS_PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-3 text-[0.95rem] text-[#d7eafb]">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-300" strokeWidth={3} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
              <a
                href={SITE.partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-yellow mt-8"
              >
                Claim Bonus
                <ArrowRight className="w-[18px] h-[18px]" strokeWidth={2.6} />
              </a>
            </div>
            <div className="glass-card-inner p-6 sm:p-8">
              <h4 className="text-[#cfe4fa] text-[0.72rem] font-bold tracking-[0.28em] uppercase">How to claim the bonus</h4>
              <ol className="mt-6 space-y-5">
                {CLAIM_STEPS.map((step, i) => (
                  <li key={step} className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-gradient-to-b from-[#fadf72] to-[#edc93f] text-[#123156] font-display font-bold text-sm flex items-center justify-center shrink-0 shadow-[0_6px_16px_rgba(180,140,20,0.35)]">
                      {i + 1}
                    </span>
                    <p className="text-[0.95rem] text-[#d7eafb] leading-relaxed pt-1">{step}</p>
                  </li>
                ))}
              </ol>
              <button type="button" onClick={copyCode} className="code-box w-full mt-8 py-5 text-center" title="Click to copy">
                <p className="text-[#9fc5e8] text-[0.68rem] font-bold tracking-[0.3em] uppercase">Bonus code</p>
                <p className="heading-3d-sm text-[1.7rem] mt-1">{SITE.code}</p>
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const LeaderboardSection = () => (
  <section className="relative z-10 py-24 sm:py-28">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          badge="Leaderboard"
          badgeIcon={<ListOrdered className="w-3.5 h-3.5" strokeWidth={2.4} />}
          title="Climb the reef"
          subtitle={`Wager on ${SITE.partner.name} under code \u201c${SITE.code}\u201d and earn insane money prizes every month.`}
        />
      </Reveal>
      <Reveal delay={150}>
        <div className="glass-card mt-14 py-16 sm:py-20 px-6 flex flex-col items-center text-center">
          <CrownIcon className="w-12 h-12 text-white/85 float-bob" />
          <h3 className="fx-liquid text-[2.6rem] sm:text-[3.6rem] mt-5">Coming soon</h3>
          <p className="mt-3 text-[#bcd9f2] text-[0.98rem]">The monthly wager race is being set up. Check back soon.</p>
          <span className="btn-pill btn-ghost mt-8 !cursor-default text-[0.78rem]">
            <Timer className="w-4 h-4" strokeWidth={2.4} />
            Stay tuned
          </span>
        </div>
      </Reveal>
      <Reveal delay={250}>
        <div className="mt-10 flex justify-center">
          <Link className="btn-pill btn-blue" to="/leaderboard">
            <Trophy className="w-[18px] h-[18px]" strokeWidth={2.4} />
            View Leaderboard
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Stream = () => (
  <section className="relative z-10 py-24 sm:py-32 scroll-mt-20 overflow-hidden" id="stream">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-70"
      aria-hidden="true"
      style={{
        backgroundImage: "url(/img/deep_scene.png)",
        maskImage: "linear-gradient(transparent 0%, black 14%, black 86%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(transparent 0%, black 14%, black 86%, transparent 100%)",
      }}
    />
    <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          badge="Live stream"
          badgeIcon={<Radio className="w-3.5 h-3.5" strokeWidth={2.4} />}
          title="Swim with the king"
          subtitle="Catch the daily dives, giveaways and big hunts live on Kick."
        />
      </Reveal>
      <Reveal delay={150}>
        <div className="relative mt-24 max-w-[880px] mx-auto">
          <img
            alt="Flounder king watching the stream"
            className="absolute -top-[92px] left-4 sm:left-10 w-28 sm:w-36 z-10 float-bob select-none pointer-events-none drop-shadow-[0_16px_30px_rgba(2,20,44,0.5)]"
            src="/img/fish_tv.png"
          />
          <div className="glass-card !rounded-[1.5rem] p-3 sm:p-4">
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]"></span>
              </div>
              <span className="text-[#9fc5e8] text-[0.68rem] font-bold tracking-[0.26em] uppercase">Kick - Player</span>
            </div>
            <div className="relative rounded-xl overflow-hidden bg-[#071a33] aspect-video">
              <iframe
                src={SITE.kickPlayerUrl}
                title="Kick live stream - flounder king"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                scrolling="no"
                allowFullScreen
              />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/45 text-[0.7rem] tracking-[0.2em] uppercase pointer-events-none">
                kick.com/{SITE.kickChannel}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-3 pt-4 pb-1">
              <div className="flex items-center gap-2.5 text-[#d7eafb] text-[0.92rem]">
                <span className="w-2 h-2 rounded-full bg-[#53fc18] shadow-[0_0_10px_rgba(83,252,24,0.8)]"></span>
                Kick · flounder king
              </div>
              <a
                href={SITE.socials.kick.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-kick !py-3 !px-6 text-[0.75rem]"
              >
                <KickIcon className="w-3.5 h-3.5" />
                Watch on Kick
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const COMMUNITY_CARDS = [
  { ...SITE.socials.x, icon: XIcon, iconSize: "w-5 h-5" },
  { ...SITE.socials.kick, icon: KickIcon, iconSize: "w-5 h-5" },
  { ...SITE.socials.discord, icon: DiscordIcon, iconSize: "w-6 h-6" },
];

const Community = () => (
  <section className="relative z-10 py-24 sm:py-28">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
      <Reveal>
        <SectionHeading badge="Community" title="Follow the flounder" />
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {COMMUNITY_CARDS.map((card, i) => (
          <Reveal key={card.label} delay={i * 120}>
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card community-card block p-7 h-full group"
            >
              <span className="w-12 h-12 rounded-2xl bg-white/8 border border-white/15 flex items-center justify-center text-white">
                <card.icon className={card.iconSize} />
              </span>
              <h3 className="font-display font-bold text-white text-[1.3rem] mt-5">{card.label}</h3>
              <p className="text-[#9fc5e8] text-[0.9rem] mt-0.5">{card.handle}</p>
              <p className="text-[#b3d2ee] text-[0.9rem] mt-3">{card.note}</p>
              <span className="inline-flex items-center gap-2 mt-6 text-white text-[0.75rem] font-bold tracking-[0.24em] uppercase">
                {card.cta}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2.4} />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 60);
      }
    }
  }, [location.hash]);

  return (
    <main>
      <Hero />
      <HowItWorks />
      <Bonus />
      <LeaderboardSection />
      <Stream />
      <Community />
    </main>
  );
};

export default Home;
